<template>
  <v-container fluid class="order-details pa-0" style="height: 95vh">
    <!-- loading skeleton -->
    <template v-if="fetchingOrderDetails">
      <div class="stack d-flex flex-column align-center">
        <v-skeleton-loader
          type="image, paragraph, button@3"
          class="loading-skeleton"
          width="400"
        />
      </div>
    </template>

    <!-- main content -->
    <template v-else>
      <!-- Invalid order details/link -->
      <template v-if="!fetchingOrderDetails && !validOrder">
        <div class="stack d-flex flex-column align-center">
          <!-- Expired link card -->
          <v-card class="expired-card" variant="flat">
            <v-icon
              class="expired-icon"
              icon="mdi-clock-outline"
              size="x-large"
            />
            <v-card-title class="expired-title"
              >This link has expired</v-card-title
            >
            <v-card-text class="expired-text px-4 py-4"
              >The download link for this order is no longer active. Links are
              available for 30 days after purchase.</v-card-text
            >
          </v-card>

          <v-card class="promo-card-expired pt-6" variant="text">
            <v-card-text>
              <div
                class="promo-title d-flex align-center justify-space-between"
              >
                <span>Unlock unlimited reports and data</span>
                <v-icon color="#51627C">mdi-shield-check-outline</v-icon>
              </div>
              <div class="promo-copy">
                Local Insight is OCSI's user-friendly platform that brings
                together over 1,500 small-area datasets for exploration. Build
                unlimited area reports around your own custom geographies and
                stay ahead with regular updates and dedicated support.
              </div>
              <v-btn
                color="primary"
                class="text-uppercase mt-4 elevation-0 w-100"
                href="https://localinsight.org/?utm_source=on-demand&utm_medium=access-order&utm_campaign=li-homepage"
                target="_blank"
              >
                Find out more
                <v-icon end>mdi-chevron-right</v-icon>
              </v-btn>
            </v-card-text>
          </v-card>
        </div>
      </template>

      <!-- order details-->
      <template v-else>
        <!-- Gradient hero -->
        <div class="hero d-flex flex-column align-center">
          <div class="hero-badge d-flex align-center justify-center">
            <v-icon color="#517A53">mdi-check</v-icon>
          </div>
          <div
            class="hero-title text-h5 text-center text-white font-weight-bold"
          >
            Your report is ready
          </div>
        </div>

        <!-- Main stack -->
        <div class="stack d-flex flex-column align-center">
          <!-- Summary card -->
          <v-card class="summary-card" elevation="2" variant="elevated">
            <v-card-text class="px-4 py-4">
              <div class="meta-grid">
                <div class="meta">
                  <div class="meta-label">Order reference</div>
                  <div class="meta-value">
                    {{ "#" + order?.order_id || "-" }}
                  </div>
                </div>
                <div class="meta">
                  <div class="meta-label">Purchase date</div>
                  <div class="meta-value">
                    {{ formatDate(order?.created_at) || "-" }}
                  </div>
                </div>
                <div class="meta">
                  <div class="meta-label">Download link expires</div>
                  <div class="meta-value">
                    {{ formatDate(order?.token_expires) || "-" }}
                  </div>
                </div>
                <div class="meta">
                  <div class="meta-label">Country</div>
                  <div class="meta-value">{{ order?.country || "-" }}</div>
                </div>
                <div class="meta">
                  <div class="meta-label">Report area</div>
                  <div class="meta-value">
                    {{ order?.area_name || "-" }}
                  </div>
                </div>
                <div class="meta">
                  <div class="meta-label">Receipt</div>
                  <div class="meta-link d-flex align-center">
                    <v-icon size="16" color="primary" class="mr-1">
                      mdi-open-in-new
                    </v-icon>
                    <a href="#" @click.prevent="onViewReceipt">View receipt</a>
                  </div>
                </div>
              </div>

              <v-divider class="my-4" />

              <div>
                <div
                  class="text-h6 text-subtitle-1 font-weight-semibold mb-1 text-left"
                >
                  Download your report
                </div>
                <div class="text-caption text-medium-emphasis mb-3 text-left">
                  Choose the format that suits your next step.
                </div>

                <div class="download-grid">
                  <!-- PDF -->
                  <div
                    class="download-tile clickable-tile"
                    :class="{ downloading: downloadingFiles.pdf }"
                    @click="onDownload('pdf')"
                  >
                    <div class="tile-row">
                      <div class="tile-icon pdf">
                        <v-icon size="20">mdi-file-pdf-box</v-icon>
                      </div>
                      <div class="tile-text">
                        <div class="tile-title">PDF</div>
                        <div class="tile-sub">Best for sharing</div>
                      </div>
                      <div class="download-icon">
                        <v-progress-circular
                          v-if="downloadingFiles.pdf"
                          indeterminate
                          size="16"
                          width="2"
                        />
                        <v-icon v-else>mdi-download</v-icon>
                      </div>
                    </div>
                  </div>

                  <!-- DOCX -->
                  <div
                    class="download-tile clickable-tile"
                    :class="{ downloading: downloadingFiles.docx }"
                    @click="onDownload('docx')"
                  >
                    <div class="tile-row">
                      <div class="tile-icon docx">
                        <v-icon size="20">mdi-file-word-box</v-icon>
                      </div>
                      <div class="tile-text">
                        <div class="tile-title">Word Document</div>
                        <div class="tile-sub">Editable version</div>
                      </div>
                      <div class="download-icon">
                        <v-progress-circular
                          v-if="downloadingFiles.docx"
                          indeterminate
                          size="16"
                          width="2"
                        />
                        <v-icon v-else>mdi-download</v-icon>
                      </div>
                    </div>
                  </div>

                  <!-- CSV -->
                  <div
                    class="download-tile clickable-tile"
                    :class="{ downloading: downloadingFiles.csv }"
                    @click="onDownload('csv')"
                  >
                    <div class="tile-row">
                      <div class="tile-icon csv">
                        <v-icon size="20">mdi-file-delimited-outline</v-icon>
                      </div>
                      <div class="tile-text">
                        <div class="tile-title">CSV</div>
                        <div class="tile-sub">Raw data</div>
                      </div>
                      <div class="download-icon">
                        <v-progress-circular
                          v-if="downloadingFiles.csv"
                          indeterminate
                          size="16"
                          width="2"
                        />
                        <v-icon v-else>mdi-download</v-icon>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-center mt-3">
                  <v-btn
                    variant="text"
                    color="primary"
                    :disabled="isAnyDownloading"
                    @click="onDownload('zip')"
                  >
                    <v-progress-circular
                      v-if="downloadingFiles.zip"
                      indeterminate
                      size="16"
                      width="2"
                      class="mr-2"
                    />
                    <v-icon v-else start>mdi-download</v-icon>
                    Download all (ZIP)
                  </v-btn>
                </div>

                <!-- Download Progress Bar -->
                <v-expand-transition>
                  <div v-if="isAnyDownloading" class="progress-container mt-4">
                    <v-progress-linear
                      :model-value="downloadProgress"
                      color="primary"
                      height="8"
                      rounded
                      class="mb-2"
                    ></v-progress-linear>
                    <div class="text-center text-caption text-medium-emphasis">
                      <span v-if="downloadProgress > 0">
                        <strong>{{ downloadProgress }}%</strong> downloaded
                      </span>
                      <span v-else>
                        <strong>Downloading...</strong>
                      </span>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </v-card-text>
          </v-card>

          <!-- Promo cards -->
          <div class="promo-row">
            <v-card class="promo-card" variant="flat">
              <v-card-text>
                <div
                  class="promo-title d-flex align-center justify-space-between"
                >
                  <span>Unlock unlimited reports and data</span>
                  <v-icon color="#51627C">mdi-shield-check-outline</v-icon>
                </div>
                <div class="promo-copy">
                  Local Insight is OCSI's user-friendly platform that brings
                  together over 1,500 small-area datasets for exploration. Build
                  unlimited area reports around your own custom geographies and
                  stay ahead with regular updates and dedicated support.
                </div>
                <v-btn
                  color="primary"
                  class="text-uppercase mt-4 elevation-0 w-100"
                  href="https://localinsight.org/?utm_source=on-demand&utm_medium=access-order&utm_campaign=li-homepage"
                  target="_blank"
                >
                  Find out more
                  <v-icon end>mdi-chevron-right</v-icon>
                </v-btn>
              </v-card-text>
            </v-card>

            <v-card class="promo-card" variant="flat">
              <v-card-text>
                <div
                  class="promo-title d-flex align-center justify-space-between"
                >
                  <span>Go deeper with a Bespoke Report</span>
                  <v-icon color="#F7B348">mdi-creation</v-icon>
                </div>
                <div class="promo-copy">
                  Get tailored geographies, custom indicators, and
                  stakeholder‑ready visuals, all designed around your
                  priorities. Includes direct access to our experts to help you
                  interpret and present results with confidence.
                </div>
                <v-btn
                  variant="outlined"
                  color="primary"
                  class="text-uppercase mt-4 w-100"
                  href="https://ocsi.uk/about/contact-us/?utm_source=on-demand&utm_medium=access-order&utm_campaign=ocsi-contact"
                  target="_blank"
                >
                  Talk to our experts
                  <v-icon end>mdi-chevron-right</v-icon>
                </v-btn>
              </v-card-text>
            </v-card>
          </div>

          <!-- Footer strip -->
          <div class="footer-block">
            <div class="powered-by text-center">
              <div class="text-caption font-italic text-medium-emphasis mb-1">
                Powered by
              </div>
              <div class="logos">
                <img
                  src="@/assets/localInsight.png"
                  alt="Local Insight"
                  height="24"
                />
                <span class="vert-divider" aria-hidden="true"></span>
                <img src="@/assets/OCSILogo.png" alt="OCSI" height="24" />
              </div>
            </div>
            <v-divider class="my-4" />
            <div class="d-flex justify-center flex-wrap ga-6 mb-2">
              <!-- support button -->
              <v-btn
                variant="text"
                color="default"
                href="mailto:support@ocsi.co.uk"
              >
                Contact support
              </v-btn>
              <!-- EULA button -->
              <v-btn
                variant="text"
                color="default"
                href="https://ocsi.uk/products/on-demand/eula/?utm_source=on-demand&utm_medium=buttont&utm_campaign=post-sale"
                target="_blank"
              >
                EULA
              </v-btn>
              <!-- privacy policy button -->
              <v-btn
                variant="text"
                color="default"
                href="https://localinsight.org/legal/privacy-policy/?utm_source=on-demand&utm_medium=buttont&utm_campaign=post-sale"
                target="_blank"
              >
                Privacy policy
              </v-btn>
            </div>
            <div
              class="disclaimer text-caption text-medium-emphasis text-center"
            >
              Local Insight™ is a trademark of Oxford Consultants for Social
              Inclusion Ltd. All rights reserved. Oxford Consultants for Social
              Inclusion, Plus X Innovation, Lewes Road, Brighton and Hove,
              Brighton BN2 4GL, registered in England and Wales, Company number:
              04800880. All other product names, company names, logos, and
              brands mentioned are the property of their respective owners and
              are used for identification purposes only. Use of these names,
              logos, or brands does not imply endorsement.
            </div>
          </div>
        </div>
      </template>
    </template>
  </v-container>
</template>

<script>
export default {
  name: "OnDemandOrderDetails",
  mounted() {
    // Send visit to analytics
    this.$axios.put("/send-analytics", {
      event: "on_demand_iod_order_visits",
    });
  },
  data() {
    return {
      orderId: null,
      token: null,
      order: null,
      validOrder: false,
      fetchingOrderDetails: false,
      downloadingFiles: {}, // Track which files are being downloaded
      downloadProgress: 0, // Track download progress percentage
      logoSrc:
        "data:image/svg+xml,%3Csvg width='131' height='45' viewBox='0 0 131 45' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.6959 24.5578C15.3319 24.5578 14.0119 24.3158 12.7579 23.8098C8.64391 22.1818 6.00391 18.2878 6.00391 13.8658C6.00391 7.96983 10.7999 3.17383 16.6959 3.17383C22.5919 3.17383 27.3879 7.96983 27.3879 13.8658C27.3879 19.1018 23.6479 23.5458 18.4779 24.4038C17.8839 24.5138 17.2899 24.5578 16.6959 24.5578ZM16.6959 5.30783C11.9659 5.30783 8.13791 9.15783 8.13791 13.8658C8.13791 17.4078 10.2719 20.5318 13.5499 21.8298C14.5619 22.2258 15.6179 22.4238 16.6959 22.4238C17.1799 22.4238 17.6639 22.3798 18.1259 22.3138C22.2619 21.6098 25.2539 18.0678 25.2539 13.8658C25.2539 9.13583 21.4039 5.30783 16.6959 5.30783Z' fill='%23C3DDD5'/%3E%3Cpath d='M27.1241 34.3258C20.1721 34.3258 14.4961 28.6718 14.4961 21.6978C14.4961 18.7278 15.5521 15.8238 17.4661 13.5578C19.8861 10.7198 23.4061 9.0918 27.1241 9.0918C34.0761 9.0918 39.7521 14.7458 39.7521 21.7198C39.7521 26.5818 36.9141 31.0698 32.5141 33.1378C30.8201 33.9078 29.0161 34.3258 27.1241 34.3258ZM27.1241 11.2038C24.0221 11.2038 21.1181 12.5678 19.1161 14.9218C17.5321 16.8138 16.6521 19.2118 16.6521 21.6758C16.6521 27.4618 21.3601 32.1698 27.1461 32.1698C28.7081 32.1698 30.2041 31.8398 31.6121 31.1798C35.2641 29.4638 37.6401 25.7238 37.6401 21.6978C37.6181 15.9118 32.9101 11.2038 27.1241 11.2038Z' fill='%2340CBCE'/%3E%3Cpath d='M16.938 41.8272C8.71 41.8272 2 35.1392 2 26.8892C2 18.6392 8.71 11.9512 16.938 11.9512C25.166 11.9512 31.876 18.6392 31.876 26.8892C31.876 35.1392 25.166 41.8272 16.938 41.8272ZM16.938 14.0852C9.876 14.0852 4.134 19.8272 4.134 26.8892C4.134 33.9512 9.876 39.6932 16.938 39.6932C24 39.6932 29.742 33.9512 29.742 26.8892C29.742 19.8272 24 14.0852 16.938 14.0852Z' fill='%236385C3'/%3E%3Cpath d='M27.3878 13.8658H25.2538C25.2538 9.13583 21.4038 5.30783 16.6958 5.30783V3.17383C22.5918 3.17383 27.3878 7.96983 27.3878 13.8658Z' fill='%23C3DDD5'/%3E%3Cpath d='M18.676 15.4948L16.938 14.2628C18.28 12.3708 20.436 10.7208 22.702 9.88477L23.45 11.8868C21.602 12.5688 19.776 13.9548 18.676 15.4948Z' fill='%2340CBCE'/%3E%3Cpath d='M16.6958 24.5581C15.3318 24.5581 14.0118 24.3161 12.7578 23.8101L13.5498 21.8301C14.5618 22.2261 15.6178 22.4241 16.6958 22.4241C17.1798 22.4241 17.6638 22.3801 18.1258 22.3141L18.4778 24.4261C17.8838 24.5141 17.2898 24.5581 16.6958 24.5581Z' fill='%23C3DDD5'/%3E%3Cpath d='M27.124 34.3252V32.1912C28.686 32.1912 30.182 31.8612 31.59 31.2012L32.492 33.1372C30.82 33.9072 29.016 34.3252 27.124 34.3252Z' fill='%2340CBCE'/%3E%3Cpath d='M54.923 19.8831C50.8046 19.8831 47.752 17.4808 47.752 13.0192C47.752 8.6299 50.7865 6.11914 54.923 6.11914C59.0594 6.11914 62.112 8.6299 62.112 13.0192C62.112 17.4085 59.0774 19.8831 54.923 19.8831ZM54.923 7.60031C51.8703 7.60031 49.5583 9.51498 49.5583 13.0192C49.5583 16.5776 51.8703 18.402 54.923 18.402C57.9756 18.402 60.3057 16.4873 60.3057 13.0192C60.3057 9.53305 57.9756 7.60031 54.923 7.60031Z' fill='%233D5769'/%3E%3Cpath d='M70.5475 19.8831C66.7362 19.8831 63.3584 17.7878 63.3584 13.0011C63.3584 8.21445 66.682 6.11914 70.5475 6.11914C72.5525 6.11914 74.3226 6.57072 75.6954 7.85319C75.8399 7.99769 75.9302 8.16026 75.9302 8.39508C75.9302 8.82859 75.6232 9.13566 75.1897 9.13566C74.8465 9.13566 74.5574 8.86472 74.3046 8.73827C73.1666 7.81706 71.7396 7.65449 70.5475 7.65449C67.6574 7.65449 65.1647 9.29823 65.1647 12.9831C65.1647 16.6137 67.6032 18.3297 70.5836 18.3297C72.2093 18.3297 73.5279 17.9685 74.72 17.0834C75.3883 16.686 75.9844 17.1918 75.9844 17.7336C75.9844 17.9323 75.8941 18.131 75.6954 18.2936C74.3046 19.4496 72.4621 19.8831 70.5475 19.8831Z' fill='%233D5769'/%3E%3Cpath d='M82.4511 19.8104C80.0668 19.8104 78.4231 19.3408 77.4838 18.817C77.2129 18.6724 77.1045 18.4196 77.1045 18.1847C77.1045 17.7332 77.5199 17.2997 78.1341 17.5345C79.2721 18.0222 80.4642 18.3834 82.3428 18.3834C84.4381 18.3834 86.0818 17.7512 86.0818 16.0894C86.0818 12.4588 77.6103 14.6083 77.6103 9.65902C77.6103 6.57024 80.8255 6.20898 82.686 6.20898C84.3658 6.20898 85.9373 6.55218 86.9127 6.96763C87.1836 7.09407 87.3101 7.34695 87.3101 7.59983C87.3101 8.03335 86.9127 8.46686 86.2624 8.23204C85.2328 7.87078 84.4561 7.6179 82.6318 7.6179C80.7713 7.6179 79.1998 7.96109 79.1998 9.62289C79.1998 13.0549 87.6533 10.9596 87.6533 16.0172C87.6352 18.9434 85.0522 19.8104 82.4511 19.8104Z' fill='%233D5769'/%3E%3Cpath d='M89.5498 7.14877C89.5498 6.62495 89.893 6.33594 90.3626 6.33594C90.8503 6.33594 91.1755 6.64301 91.1755 7.14877V18.8717C91.1755 19.3955 90.7962 19.6845 90.3626 19.6845C89.9111 19.6845 89.5498 19.3774 89.5498 18.8717V7.14877Z' fill='%233D5769'/%3E%3Cpath d='M56.7626 31.5192C56.7626 32.4595 56.5865 33.2649 56.2342 33.9354C55.8848 34.603 55.4075 35.1143 54.8024 35.4695C54.2001 35.8246 53.5169 36.0021 52.7527 36.0021C51.9885 36.0021 51.3038 35.8246 50.6987 35.4695C50.0964 35.1115 49.6191 34.5987 49.2669 33.9311C48.9174 33.2607 48.7427 32.4567 48.7427 31.5192C48.7427 30.5788 48.9174 29.7749 49.2669 29.1072C49.6191 28.4368 50.0964 27.924 50.6987 27.5689C51.3038 27.2138 51.9885 27.0362 52.7527 27.0362C53.5169 27.0362 54.2001 27.2138 54.8024 27.5689C55.4075 27.924 55.8848 28.4368 56.2342 29.1072C56.5865 29.7749 56.7626 30.5788 56.7626 31.5192ZM55.1731 31.5192C55.1731 30.8572 55.0694 30.299 54.862 29.8445C54.6575 29.3871 54.3734 29.0419 54.0098 28.8089C53.6461 28.5732 53.2271 28.4553 52.7527 28.4553C52.2782 28.4553 51.8592 28.5732 51.4956 28.8089C51.1319 29.0419 50.8464 29.3871 50.639 29.8445C50.4345 30.299 50.3322 30.8572 50.3322 31.5192C50.3322 32.1811 50.4345 32.7408 50.639 33.1982C50.8464 33.6527 51.1319 33.9979 51.4956 34.2337C51.8592 34.4666 52.2782 34.5831 52.7527 34.5831C53.2271 34.5831 53.6461 34.4666 54.0098 34.2337C54.3734 33.9979 54.6575 33.6527 54.862 33.1982C55.0694 32.7408 55.1731 32.1811 55.1731 31.5192ZM65.4292 27.1555V35.8828H64.0229L59.9107 29.9382H59.8382V35.8828H58.2573V27.1555H59.6721L63.78 33.1044H63.8567V27.1555H65.4292ZM73.0858 35.8828H70.1284V27.1555H73.1454C74.0119 27.1555 74.7562 27.3303 75.3784 27.6797C76.0034 28.0263 76.4835 28.5249 76.8187 29.1754C77.1539 29.826 77.3216 30.6044 77.3216 31.5107C77.3216 32.4197 77.1525 33.201 76.8145 33.8544C76.4792 34.5078 75.9949 35.0092 75.3613 35.3587C74.7306 35.7081 73.9721 35.8828 73.0858 35.8828ZM71.7093 34.5149H73.0091C73.617 34.5149 74.1241 34.4041 74.5304 34.1825C74.9366 33.9581 75.242 33.6243 75.4466 33.1811C75.6511 32.7351 75.7534 32.1783 75.7534 31.5107C75.7534 30.843 75.6511 30.2891 75.4466 29.8487C75.242 29.4055 74.9395 29.0746 74.5389 28.8558C74.1412 28.6342 73.6468 28.5234 73.0559 28.5234H71.7093V34.5149ZM78.8237 35.8828V27.1555H84.4998V28.4808H80.4047V30.8501H84.2058V32.1754H80.4047V34.5575H84.5339V35.8828H78.8237ZM86.1127 27.1555H88.0474L90.6383 33.4794H90.7406L93.3315 27.1555H95.2662V35.8828H93.7491V29.8871H93.6681L91.2562 35.8572H90.1227L87.7108 29.8743H87.6298V35.8828H86.1127V27.1555ZM98.1085 35.8828H96.421L99.4934 27.1555H101.445L104.522 35.8828H102.834L100.503 28.9453H100.435L98.1085 35.8828ZM98.1639 32.4609H102.766V33.7308H98.1639V32.4609ZM112.843 27.1555V35.8828H111.437L107.325 29.9382H107.252V35.8828H105.671V27.1555H107.086L111.194 33.1044H111.271V27.1555H112.843ZM117.523 35.8828H114.566V27.1555H117.583C118.449 27.1555 119.194 27.3303 119.816 27.6797C120.441 28.0263 120.921 28.5249 121.256 29.1754C121.591 29.826 121.759 30.6044 121.759 31.5107C121.759 32.4197 121.59 33.201 121.252 33.8544C120.917 34.5078 120.432 35.0092 119.799 35.3587C119.168 35.7081 118.41 35.8828 117.523 35.8828ZM116.147 34.5149H117.447C118.055 34.5149 118.562 34.4041 118.968 34.1825C119.374 33.9581 119.68 33.6243 119.884 33.1811C120.089 32.7351 120.191 32.1783 120.191 31.5107C120.191 30.843 120.089 30.2891 119.884 29.8487C119.68 29.4055 119.377 29.0746 118.976 28.8558C118.579 28.6342 118.084 28.5234 117.493 28.5234H116.147V34.5149Z' fill='%233D5769'/%3E%3C/svg%3E%0A",
    };
  },
  computed: {
    isAnyDownloading() {
      return Object.values(this.downloadingFiles).some(
        (downloading) => downloading,
      );
    },
  },
  created() {
    this.fetchOrderDetails();
  },
  watch: {
    "$route.params.orderId"() {
      this.fetchOrderDetails();
    },
    "$route.query.token"() {
      this.fetchOrderDetails();
    },
  },
  methods: {
    formatDate(ts) {
      try {
        if (!ts) return null;
        const d = new Date(ts);
        if (isNaN(d.getTime())) return null;
        return new Intl.DateTimeFormat("en-GB", {
          dateStyle: "long",
          timeZone: "Europe/London",
        }).format(d);
      } catch (e) {
        return null;
      }
    },
    extractParams() {
      this.orderId = this.$route?.params?.orderId ?? null;
      this.token = this.$route?.query?.ordertoken ?? null;
    },
    async fetchOrderDetails() {
      this.extractParams();
      this.fetchingOrderDetails = true;
      //we can only continue if we have an order id and a token to access the details
      if (this.orderId && this.token) {
        try {
          const response = await this.$axios.get(
            `/on-demand-order-details/${this.orderId}/${this.token}`,
          );
          this.order = response.data;
          this.validOrder = true;
        } catch (e) {
          console.warn("Error fetching order details", e);
          this.validOrder = false;
        } finally {
          this.fetchingOrderDetails = false;
        }
        return;
      }

      //order id or token missing
      this.validOrder = false;
      this.fetchingOrderDetails = false;
    },
    onViewReceipt() {
      if (!this.orderId || !this.token) {
        console.error("Missing order ID or token for receipt");
        return;
      }

      this.$axios
        .get(`/on-demand-receipt/${this.orderId}/${this.token}`)
        .then((response) => {
          const receiptUrl = response.data.receipt_url;
          if (receiptUrl) {
            // Open receipt in a new tab
            window.open(receiptUrl, "_blank");
          } else {
            console.error("No receipt URL found in response");
          }
        })
        .catch((error) => {
          console.error("Error fetching receipt:", error);
        });
    },
    onDownload(kind) {
      // Prevent download if any download is already in progress
      if (this.isAnyDownloading) {
        return;
      }

      // Set loading state and reset progress
      this.downloadingFiles[kind] = true;
      this.downloadProgress = 0;

      this.$axios({
        method: "GET",
        url: `/on-demand-download-file/${kind}/${this.orderId}/${this.token}`,
        responseType: "blob", // Handle binary data
        onDownloadProgress: (progressEvent) => {
          // Update progress if we have total size
          if (progressEvent.total && progressEvent.total > 0) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            this.downloadProgress = percentCompleted;
          } else {
            // Fallback: estimate progress based on typical file sizes
            const estimatedSizes = {
              csv: 18 * 1024, // ~18KB
              pdf: 5.9 * 1024 * 1024, // ~5.9MB
              docx: 37 * 1024 * 1024, // ~37MB
              zip: 42 * 1024 * 1024, // ~42MB
            };

            const estimatedSize = estimatedSizes[kind];
            if (estimatedSize && progressEvent.loaded > 0) {
              const estimatedPercent = Math.min(
                95,
                Math.round((progressEvent.loaded / estimatedSize) * 100),
              );
              this.downloadProgress = estimatedPercent;
            } else {
              this.downloadProgress = 0;
            }
          }
        },
      })
        .then((response) => {
          // Log file size from headers
          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;

          // Get filename from response headers or use a default
          const contentDisposition = response.headers["content-disposition"];
          let filename = `${kind}_report.${kind}`;
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
              filename = filenameMatch[1];
            }
          }

          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // Send download to analytics
          this.$axios.put("/send-analytics", {
            event: "on_demand_iod_order_downloads",
            file_type: kind,
          });
        })
        .catch(async (error) => {
          // Extract error message from backend response
          let errorMessage = "An unexpected error occurred during download";

          if (error.response?.data) {
            // If the response data is a Blob (because we're using responseType: "blob")
            if (error.response.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const parsedData = JSON.parse(text);
                if (parsedData.message) {
                  errorMessage = parsedData.message;
                }
              } catch (e) {
                errorMessage = "Server error occurred";
              }
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.emit.emit("systemMessage", {
            title: `Error! Failed to Download Report.`,
            message: errorMessage,
            timeout: -1,
            colour: "error",
          });
        })
        .finally(() => {
          // Clear loading state and reset progress
          this.downloadingFiles[kind] = false;
          this.downloadProgress = 0;
        });
    },
  },
};
</script>

<style scoped>
.order-details {
  background: #f9f9f9;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100vh; /* ensure internal scrolling regardless of parent */
}
.on-demand {
  color: #3d5769;
}
.hero {
  position: absolute;
  left: 0;
  right: 0;
  height: 335px;
  background: linear-gradient(98.1deg, #778aa6 6.49%, #51627c 103.14%);
  isolation: isolate;
  padding-top: 32px;
  z-index: 0;
}
.hero-badge {
  width: 64px;
  height: 64px;
  border-radius: 100px;
  background: #c6ecc6;
}
.hero-title {
  margin-top: 12px;
}
.stack {
  padding-top: 160px; /* creates overlap over hero */
  min-height: calc(
    100vh - 32px - 32px - 62px - 16px
  ); /* viewport minus top-navs */
  display: flex;
  flex-direction: column;
}
.summary-card {
  width: 640px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.meta-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 16px;
}
.meta-label {
  font-size: 12px;
  line-height: 15px;
  color: #7f7f7f;
  text-align: left;
}
.meta-value {
  font-size: 14px;
  line-height: 17px;
  color: #4d4d4d;
  text-align: left;
}
.meta-link a {
  color: #0e5b99;
  text-decoration: none;
}
.download-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.download-tile {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px;
}

.clickable-tile {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.clickable-tile:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.2);
}

.clickable-tile.downloading {
  cursor: not-allowed;
  opacity: 0.7;
}

.clickable-tile.downloading:hover {
  background-color: transparent;
  border-color: rgba(0, 0, 0, 0.12);
}
.tile-row {
  display: flex;
  align-items: center;
  text-align: start;
  gap: 8px;
  flex-wrap: nowrap;
}
.tile-icon {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tile-icon.pdf {
  background: rgba(179, 13, 20, 0.1);
  color: #b30d14;
}
.tile-icon.docx {
  background: rgba(23, 96, 194, 0.1);
  color: #1760c2;
}
.tile-icon.csv {
  background: rgba(17, 129, 71, 0.1);
  color: #118147;
}
.tile-text {
  flex: 1;
  min-width: 0;
  text-align: left;
}
.tile-title {
  font-size: 12px;
  font-weight: 600;
  color: #4d4d4d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tile-sub {
  font-size: 12px;
  color: #7f7f7f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.download-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #666;
  transition: color 0.2s ease-in-out;
}

.clickable-tile:hover .download-icon {
  color: #1976d2;
}

.promo-row {
  display: grid;
  grid-template-columns: repeat(2, 312px);
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 32px;
}
.promo-card {
  width: 312px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.promo-card-expired {
  width: 420px;
}
.promo-title {
  font-size: 14px;
  font-weight: 600;
  color: #4d4d4d;
}
.promo-copy {
  font-size: 12px;
  color: #4d4d4d;
  margin-top: 8px;
  text-align: left;
}

.footer-block {
  max-width: 1024px;
  /* padding: 24px 0; */
  padding-bottom: 24px;
  margin-top: auto;
}
.disclaimer {
  max-width: 1024px;
  margin: 0 auto;
}
.logos {
  display: inline-flex;
  align-items: center;
  gap: 16px;
}
.vert-divider {
  display: inline-block;
  width: 1px;
  height: 24px;
  background: rgba(0, 0, 0, 0.12);
}
.expired-card {
  width: 420px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding-top: 32px;
}
.expired-icon {
  height: 64px;
  width: 64px;
  color: #e09237;
  background-color: #fdd9af;
  border-radius: 32px;
}
.expired-title {
  font-size: 20px;
  font-weight: 600;
}
.expired-text {
  font-size: 16px;
}

@media (max-width: 960px) {
  .summary-card {
    width: 95%;
  }
  .meta-grid {
    grid-template-columns: 1fr 1fr;
  }
  .download-grid {
    grid-template-columns: 1fr;
  }
  .promo-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-template-columns: none;
  }
  .promo-card {
    width: 95%;
  }
  .footer-block {
    width: 95%;
    padding: 16px;
  }
}

/* Smooth progress bar transitions */
.progress-container {
  transition: all 0.3s ease-in-out;
}

/* Custom transition for smoother appearance */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease-in-out;
}

.v-expand-transition-enter-from,
.v-expand-transition-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
